import {inject, injectable} from "inversify";
import {TransferOffer} from "@/modules/transfer-market/entity";
import {TransferOfferStatusEnum} from "@/modules/transfer-market/enums";
import {BankCategory} from "@/modules/finances/enums";
import {EventEmitter} from "@/modules/base/events";
import {BeforeTransferClosedEvent} from "@/modules/transfer-market/events/BeforeTransferClosedEvent";
import {AfterTransferClosedEvent} from "@/modules/transfer-market/events/AfterTransferClosedEvent";
import {NegotiationCloser} from "@/modules/diplomacy/services/NegotiationCloser";
import {BeforeTransferAbortedEvent} from "@/modules/transfer-market/events/BeforeTransferAbortedEvent";
import {AfterTransferAbortedEvent} from "@/modules/transfer-market/events/AfterTransferAbortedEvent";
import PlayerTransferrer from "@/modules/transfer-market/services/PlayerTransferrer";
import {NegotiationStatusEnum} from "@/modules/diplomacy/enums";
import {Player} from "@/modules/team/entity";
import GateManager from "@/modules/action-points/services/GateManager";
import {GateTypeEnum} from "@/modules/action-points/enums";
import {AfterTransferEvent} from "@/modules/transfer-market/events/AfterTransferEvent";
import {BeforeTransferEvent} from "@/modules/transfer-market/events/BeforeTransferEvent";

@injectable()
export class TransferOfferHandler {
  constructor(
    @inject(EventEmitter) private em: EventEmitter,
    @inject(GateManager) private gateManager: GateManager,
    @inject(NegotiationCloser) private negotiationCloser: NegotiationCloser,
    @inject(PlayerTransferrer) private playerTransferrer: PlayerTransferrer,
  ) {}

  async abort(offer: TransferOffer) {
    await this.em.emitAsync(new BeforeTransferAbortedEvent(offer));

    if (offer.teamNegotiation) {
      await this.negotiationCloser.abort(offer.teamNegotiation);
    }

    await this.negotiationCloser.abort(offer.playerNegotiation);

    await this.em.emitAsync(new AfterTransferAbortedEvent(offer));
  }

  async close(offer: TransferOffer) {
    if (offer.team.user && offer.team.balance < offer.once) {
      throw 'Not enough money to close this offer.';
    }

    if (
      offer.teamNegotiation && offer.teamNegotiation.status === NegotiationStatusEnum.OPENED && !offer.teamNegotiation.canBeClosed
      || offer.playerNegotiation.status === NegotiationStatusEnum.OPENED && !offer.playerNegotiation.canBeClosed
    ) {
      throw 'One of the negotiations can not be closed.';
    }

    await this.em.emitAsync(new BeforeTransferClosedEvent(offer));

    const player = offer.player;

    await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.change(
      offer.team.user ? -offer.once : offer.once,
      {
        category: BankCategory.TRANSFERS,
        label: translator.t('transfer_of_player_x', {player: player.shortName}),
      }
    );

    await this.playerTransferrer.transfer(player, offer.team, offer.once);
    player.wage = offer.playerNegotiation.monthly;

    if (offer.teamNegotiation && offer.teamNegotiation.status === NegotiationStatusEnum.OPENED && offer.teamNegotiation.canBeClosed) {
      await this.negotiationCloser.close(offer.teamNegotiation);
    }

    if (offer.playerNegotiation.status === NegotiationStatusEnum.OPENED && offer.playerNegotiation.canBeClosed) {
      await this.negotiationCloser.close(offer.playerNegotiation);
    }

    if (offer.listing) {
      await offer.listing.remove();
    }

    // todo: abort all other offers

    offer.status = TransferOfferStatusEnum.CLOSED;
    await offer.save();

    await this.closeAllOffersForPlayer(player);

    await player.save();

    await this.em.emitAsync(new AfterTransferClosedEvent(offer));
  }

  private async closeAllOffersForPlayer(player: Player) {
    const offers = await TransferOffer
      .createQueryBuilder('offer')
      .innerJoinAndSelect('offer.player', 'player')
      .leftJoinAndSelect('offer.teamNegotiation', 'teamNegotiation')
      .innerJoinAndSelect('offer.playerNegotiation', 'playerNegotiation')
      .where('player.id = :playerId', {playerId: player.id})
      .andWhere('offer.status = :offerStatus', {offerStatus: TransferOfferStatusEnum.OPENED})
      .getMany();

    await offers.forEachAsync((offer: TransferOffer) => this.abort(offer));
  }
}